import { cardPresentation } from './cardPresentation';

/*
 * Adapters from the Linkpass APIs to the shapes the existing components consume, so the
 * pages stay unchanged and there is one place to look when a field goes missing.
 *
 * Several endpoints answer in two shapes, captured from leap-uat and from preprod. Both are
 * read because we cannot yet say which is canonical - that is why so many fields below are
 * a `??` chain rather than a single name.
 */

/* Returns '' on anything unparseable, so callers drop the value rather than hand date-fns
 * something it throws a RangeError on. */
export const toYyyymmddLoose = (v) => {
  if (!v) {
    return '';
  }
  if (/^\d{8}$/.test(v)) {
    return v;
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  // UTC getters: local time (SGT) would roll anything after 16:00Z into the next day
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}${month}${day}`;
};

// addSlashes() slices positionally and expects DDMMYYYY
export const toDdmmyyyy = (v) => {
  if (!v) {
    return '';
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (m) {
    return `${m[3]}${m[2]}${m[1]}`;
  }
  if (/^\d{8}$/.test(v)) {
    return v;
  }
  return '';
};

/*
 * Derived because the new API stopped sending it. Deliberately narrow - the note claims to
 * a member when their points expire, and a wrong date is worse than no note. P01 only, and
 * 30 Jun only; anything else returns null and the line disappears, which is visible.
 * Delete this if the API starts returning the field again.
 */
export const unionExpiryNote = (yyyymmdd, poolId) => {
  if (poolId !== 'P01') {
    return null;
  }
  if (!/^\d{4}0630$/.test(yyyymmdd ?? '')) {
    return null;
  }
  return `(*31 Dec ${yyyymmdd.slice(0, 4)} for NTUC Union members)`;
};

/* MyTransactions keys its columns off these exact strings; an unmapped type leaves all
 * three blank with no error. */
const TXN_TYPE = {
  Accrual: 'AWD',
  Redemption: 'RED',
  'Manual Points Adjustment': 'ADJ',
};

/*
 * /v1 sends `quantity` unsigned and the component prints it as-is, so a redemption would
 * read "2.00" where the old API sent "-3000". Adjustments pass through untouched - the
 * sample held only a positive one, so there is no evidence how a negative one arrives.
 */
const signedQuantity = (type, quantity) => {
  if (type !== 'RED') {
    return quantity;
  }
  if (quantity === null || quantity === undefined || quantity === '') {
    return quantity;
  }
  const n = Number(quantity);
  return Number.isNaN(n) || n <= 0 ? quantity : String(-n);
};

/*
 * GET /v1/me/transactions -> the old /v4/transactionHistory shape. /v1 carries no balance
 * block, so the pool list comes from the balance endpoint the page fetches alongside.
 */
export const toTransactionHistory = (res) => {
  const pool = res?.balance?.pool;
  const rows = Array.isArray(res) ? res : res?.transactions ?? res?.data ?? [];
  return {
    // neither payload states a window; the copy reads "past {months} months"
    months: 6,
    transactions: rows.map((t) => {
      const transactionType = TXN_TYPE[t.type] ?? t.type ?? t.transactionType;
      return {
        date: toYyyymmddLoose(t.dateTime ?? t.createdAt ?? t.date),
        description: t.description,
        transactionType,
        amount: signedQuantity(transactionType, t.quantity ?? t.poolUnit ?? t.amount),
        referenceNo: t.txnNo ?? t.referenceNo,
      };
    }),
    userBalance: {
      balance: pool?.balance,
      lpToDollar: pool?.dollarValue,
      pools:
        pool && toYyyymmddLoose(pool.expiryDate)
          ? [
              {
                balance: pool.balance,
                expiryDate: toYyyymmddLoose(pool.expiryDate),
                poolId: pool.poolId,
                lpExpiryString: unionExpiryNote(toYyyymmddLoose(pool.expiryDate), pool.poolId),
              },
            ]
          : [],
    },
    // nothing renders this today; carried through in case we surface it later
    spendTracker: res?.spendTracker,
  };
};

/* The profile page's headline figure. */
export const toLinkpointsBalance = (res) => {
  // one shape wraps the figures in `pool`, the other returns them flat
  const pool = res?.pool ?? res ?? {};
  return {
    lpToDollar: pool.dollarValue,
    userBalance: { balance: pool.balance },
  };
};

/*
 * This endpoint returns the LinkPoints pool only, so a member with a FoodFare balance no
 * longer sees it listed - a deliberate consequence of using it, not an oversight. poolId is
 * absent and safely assumed P01, which unionExpiryNote needs to identify the pool.
 */
export const toLinkpointsUserBalance = (res) => {
  const src = res?.pool ?? res ?? {};
  const expiryDate = toYyyymmddLoose(src.expiryDate);
  return {
    balance: src.balance,
    lpToDollar: src.dollarValue,
    pools: expiryDate
      ? [
          {
            /*
             * expiringBalance, not balance: only part of the pool expires on expiryDate, and
             * the line names a figure the member will lose. Falls back to the total for a
             * response that does not carry it.
             */
            balance: src.expiringBalance ?? src.balance,
            expiryDate,
            poolId: src.poolId ?? 'P01',
            poolName: src.poolName ?? 'LinkPoints',
            lpExpiryString: unionExpiryNote(expiryDate, src.poolId ?? 'P01'),
          },
        ]
      : [],
  };
};

/*
 * The old API resolved card artwork and copy server-side; the new one returns only the card
 * record, so utils/cardPresentation.js maps it locally - see the trade-off recorded there.
 *
 * /v1 does not send cardType, the key that mapping is held against, but it is the first six
 * digits of the card number (8202000034224435 -> 820200), which held for all nine rows of
 * the captured response. Prefer an API-supplied cardType if one ever returns.
 */
export const cardTypeOf = (c) => {
  if (c?.cardType) {
    return c.cardType;
  }
  const n = c?.cardNumber;
  return typeof n === 'string' && n.length >= 6 ? n.slice(0, 6) : undefined;
};

/*
 * Keyed on cardType rather than /v1's coarser `type`: 820200 (Trust Visa) and 820100 (OCBC
 * Plus! Visa) name different banks in their replacement copy, so a grouping like "Payment
 * card" risks telling a Trust cardholder to ring OCBC.
 *
 * An unmapped cardType falls back to showing `type` as the name, with no artwork and no
 * replacement copy - something the API actually said, rather than a guessed bank.
 */
const presentationFor = (c, cardType) => {
  const mapped = cardPresentation(cardType, c.cardNumber);
  if (mapped.cardName) {
    return mapped;
  }
  return c.type ? { cardName: c.type } : mapped;
};

export const toPhysicalCards = (res) =>
  (Array.isArray(res) ? res : res?.cards ?? []).map((c) => {
    const cardType = cardTypeOf(c);
    return {
      CardNumber: c.cardNumber,
      NameOnCard: c.nameOnCard,
      // /v1 renamed these and sends ISO dates where the old response sent yyyyMMdd
      CardStatus: c.cardStatus ?? c.status,
      CardExpiryDate: toYyyymmddLoose(c.cardExpiryDate ?? c.expiryDate),
      CardIssueDate: toYyyymmddLoose(c.cardIssueDate ?? c.issueDate),
      PALastUpdateDate: toYyyymmddLoose(c.lastUpdateDate),
      PATerminationDate: toYyyymmddLoose(c.terminationDate),
      // absent from /v1 entirely; nothing in link.sg renders them today
      CardBlockCode: c.cardBlockCode,
      CardReasonCode: c.cardReasonCode,
      cardType,
      type: c.type,
      ...presentationFor(c, cardType),
    };
  });

/*
 * The exchange listing page's partner cards.
 *
 * The API's own `permalink` is deliberately NOT emitted. The detail page recovers the id
 * with permalink.split('-')[0] against an href built as `${id}-${slug}`, so a bare permalink
 * like "xR0tkPRQQ" would make that split return the whole string and the lookup fail.
 *
 * A missing status means the uat shape, which omits the field; anything else must be ACTIVE.
 */
export const toExchangePartners = (res) => {
  const rows = Array.isArray(res) ? res : res?.exchanges ?? res?.data ?? [];
  return rows
    .filter((p) => p.status === undefined || p.status === 'ACTIVE')
    .map((p) => ({
      id: p.id,
      merchantName: p.name ?? p.partnerName,
      merchantLogoUrl: p.logoUrl ?? p.imgUrls?.logo,
      bigUrl: p.bigBannerUrl ?? p.imgUrls?.banner,
      shortConversionRateText: p.conversionRate ?? p.shortRateText ?? p.rateText,
      rank: p.rank,
    }))
    .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
};

/* Arrives as an array on one partner and an empty OBJECT on another, so mapping it throws. */
const toFormFields = (v) => (Array.isArray(v) ? v : []);

/* /pex names the points sourcePoints/destinationPoints; the components read
 * source/destination. Both are emitted so either consumer works. */
const toRates = (rates) =>
  (rates ?? []).map((r) => ({
    ...r,
    source: r.sourcePoints ?? r.source,
    destination: r.destinationPoints ?? r.destination,
  }));

/*
 * getServerSideProps refuses to serialize `undefined`, so any field the API omits would 500
 * the whole page. Dropping the empty keys fixes that class of bug once, rather than a
 * `?? null` per field that the next optional one would miss.
 */
const stripUndefined = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, stripUndefined(v)]),
  );
};

export const toPartnerData = (res, permalink) => {
  const user = res?.user ?? {};
  const config = res?.config ?? {};
  return stripUndefined({
    id: res?.id,
    merchantName: res?.name ?? res?.partnerName,
    merchantLogoUrl: res?.logoUrl ?? res?.imgUrls?.logo,
    bannerUrl: res?.bannerUrl ?? res?.imgUrls?.banner,
    bigUrl: res?.bigBannerUrl ?? res?.imgUrls?.banner,
    description: res?.description,
    tnc: res?.tnc,
    metaTitle: res?.metaTitle,
    metaDescription: res?.metaDescription,
    shortConversionRateText: res?.conversionRate ?? res?.shortRateText ?? res?.rateText,
    transactionDurationNote: res?.transactionNote,
    permalink: permalink ?? res?.permalink,
    rates: toRates(res?.rates),
    // decide which rates are affordable, see categorizeRates
    availableLpBalance: user.linkpointsBalance,
    pointExcMerchantBal: user.partnerPointsBalance,
    config: {
      linkPartnerText: config.linkagePartnerText ?? res?.button?.linkButtonText,
      merchantExchangeText: config.partnerSiteRedirectionText ?? res?.button?.partnerButtonText,
      merchantExchangeUrl: config.partnerSiteRedirectionUrl ?? res?.button?.partnerButtonUrl,
      formFields: toFormFields(config.formFields ?? res?.associationFields),
    },
    loggedInPlus: user.loggedInPlus,
    loggedInMerchant: user.loggedInPartner,
    externalId: user.externalId,
    vc: user.vc,
    // the two shapes state this as opposites; normalised to "can unlink"
    isDelinkEnable:
      user.isDelinkDisable !== undefined ? !user.isDelinkDisable : res?.isDelinkEnable,
    swapUrl: res?.urls?.swap,
    associationType: res?.associationType,
    conversionType: res?.conversionType,
    status: res?.status,
    currency: res?.currency,
    currencyCode: res?.currencyCode,
  });
};

/*
 * Failure copy arrives prefixed with an internal code - "[N3005 Link Already Exists] We are
 * unable to..." - which means nothing to a member and reads like a fault. The prose after it
 * names the partner's support channel and is the part worth showing.
 */
export const stripErrorCode = (message) =>
  typeof message === 'string' ? message.replace(/^\s*\[[^\]]*\]\s*/, '') : '';

/* Debit means Linkpoints left the account, which the table shows as a negative. */
export const toExchangeTransactions = (res) =>
  (Array.isArray(res) ? res : res?.transactions ?? res?.data ?? []).map((t) => ({
    id: t.transactionNo ?? t.referenceId,
    date: t.activityDate,
    description: t.description,
    points: t.type === 'Debit' ? -Number(t.linkpoints) : Number(t.linkpoints),
  }));

/*
 * block_number maps to buildingCode because the old response carried a block there ("2M").
 * Everything falls back to '' - the address is absent on some accounts, and formattedAddress()
 * then shows its "-" placeholder rather than "undefined". latitude and longitude are ignored;
 * the API sends them as the string "null".
 */
const toAddressFields = (a) => ({
  buildingName: a?.building_name ?? '',
  buildingCode: a?.block_number ?? '',
  street: a?.road_name ?? '',
  buildingFloor: a?.floor_number ?? '',
  buildingUnit: a?.unit_number ?? '',
  postalCode: a?.postal_code ?? '',
});

/* The payload is snake_case; the page expects camelCase. */
export const toUserProfile = (u) => ({
  ...u,
  firstName: u?.firstName ?? u?.name ?? '',
  lastName: u?.lastName ?? '',
  // some accounts carry no top-level phone_number and only have the MyInfo one
  contactNumber:
    u?.phone_number ?? u?.primaryPhoneNumber ?? u?.singpass?.myinfo?.data?.mobileNo?.nbr ?? '',
  email: u?.email ?? '',
  // addSlashes() expects DDMMYYYY; the API sends ISO "1976-04-25"
  dob: toDdmmyyyy(u?.date_of_birth ?? u?.dateOfBirth),
  ...toAddressFields(u?.residential_address),
});

/*
 * The `channels` level matters: reading preferences[brand].email directly gives undefined,
 * which rendered every toggle as opted out regardless of what the member had chosen.
 *
 * linkplus, not fairprice - link.sg is the Link (formerly Plus!) programme, and only the
 * linkplus brand carries the `mail` channel that the Direct Mail row needs.
 */
export const toCommSetting = (u, brand = 'linkplus') => {
  const p = u?.preferences?.[brand] ?? {};
  const channels = p.channels ?? p;
  const flag = (v) => (v ? 1 : 0);
  return {
    optInEmail: flag(channels.email),
    optInDirectMail: flag(channels.mail),
    optInSms: flag(channels.text),
    optInVoice: flag(channels.voice),
    optInPush: 0,
  };
};
